import { useEffect, useState } from 'react'
import { get } from '../lib/api'

interface Seat { id: string; deck: string; row_label: string; number: number; near_door: number; is_accessible: number; taken: boolean }

/**
 * Choose where to sit.
 *
 * Shown to residents only, and that is a product decision rather than a
 * technical one: somebody who crosses twice a week has a seat they want and a
 * reason for it, and a visitor making one trip does not benefit from a decision
 * they have no basis to make. Visitors are assigned — which is also how the
 * grouping and priority rules stay enforceable.
 */
export default function SeatMap({ sailingId, need, chosen, onChange }: {
  sailingId: string
  need: number
  chosen: string[]
  onChange: (ids: string[]) => void
}) {
  const [seats, setSeats] = useState<Seat[] | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    get<{ seats: Seat[] }>(`/api/sailings/${sailingId}/seats`)
      .then((d) => setSeats(d.seats))
      .catch((e: Error) => setError(e.message))
  }, [sailingId])

  if (error) return <p className="error">{error}</p>
  if (!seats) return <p className="muted">Loading the deck plan…</p>

  const decks = [...new Set(seats.map((s) => s.deck))]

  const toggle = (seat: Seat) => {
    if (seat.taken) return
    if (chosen.includes(seat.id)) onChange(chosen.filter((id) => id !== seat.id))
    // Drop the oldest rather than refusing the tap. Somebody who has picked
    // their whole party and then sees a better row should not have to work out
    // which seat to release first.
    else onChange([...chosen, seat.id].slice(-need))
  }

  return (
    <div className="seatmap">
      <p className="muted small">
        Choose {need} {need === 1 ? 'seat' : 'seats'} — {chosen.length} chosen.
      </p>

      {decks.map((deck) => {
        const rows = [...new Set(seats.filter((s) => s.deck === deck).map((s) => s.row_label))]
        return (
          <div key={deck} className="deck">
            <h4>{deck === 'main' ? 'Main deck' : deck[0]!.toUpperCase() + deck.slice(1)}</h4>
            {rows.map((label) => (
              <div key={label} className="seat-row">
                <span className="row-label">{label}</span>
                {seats.filter((s) => s.deck === deck && s.row_label === label)
                  .sort((a, b) => a.number - b.number)
                  .map((seat) => (
                    <button
                      key={seat.id}
                      type="button"
                      onClick={() => toggle(seat)}
                      disabled={seat.taken}
                      aria-pressed={chosen.includes(seat.id)}
                      className={[
                        'seat',
                        seat.taken ? 'taken' : '',
                        chosen.includes(seat.id) ? 'chosen' : '',
                        seat.near_door ? 'near-door' : '',
                        seat.is_accessible ? 'accessible' : '',
                      ].join(' ').trim()}
                      title={[
                        `${label}${seat.number}`,
                        seat.near_door ? 'by the door' : '',
                        seat.is_accessible ? 'accessible' : '',
                        seat.taken ? 'taken' : '',
                      ].filter(Boolean).join(' · ')}
                    >
                      {seat.number}
                    </button>
                  ))}
              </div>
            ))}
          </div>
        )
      })}

      <ul className="legend muted small">
        <li><span className="swatch near-door" /> By a door</li>
        <li><span className="swatch accessible" /> Accessible</li>
        <li><span className="swatch taken" /> Taken</li>
      </ul>
    </div>
  )
}
